/**
 * API конструктора шаблонов карточек знака Визуализатора.
 */

import { del, get, post, put } from "@/lib/api-client-v2";
import type {
  TacticalFeatureCardTemplateListResponse,
  TacticalFeatureCardTemplateWithDetails,
  TacticalFeatureCardFieldType,
} from "@/types/tactical-feature-card-template";
import type {
  CardFieldWidgetTypeItem,
  CardTemplateImportResponse,
  CardTemplatePublishRequest,
  CardTemplatePublishResponse,
  CardTemplateRestoreRequest,
} from "@/types/card-template";

export interface TacticalFeatureCardTemplateCreatePayload {
  code: string;
  name: string;
  description?: string | null;
  feature_kind?: string | null;
  symbol_kls_id?: number | null;
  organization_kls_id?: number | null;
  is_default?: boolean;
}

export interface TacticalFeatureCardTemplateUpdatePayload {
  name?: string;
  description?: string | null;
  feature_kind?: string | null;
  symbol_kls_id?: number | null;
  organization_kls_id?: number | null;
  is_default?: boolean;
  is_active?: boolean;
}

export interface TacticalFeatureCardSectionPayload {
  code: string;
  name: string;
  display_order?: number;
  is_collapsible?: boolean;
  is_default_open?: boolean;
  ui_config?: Record<string, unknown>;
}

export interface TacticalFeatureCardSectionUpdatePayload {
  code?: string;
  name?: string;
  display_order?: number;
  is_collapsible?: boolean;
  is_default_open?: boolean;
  ui_config?: Record<string, unknown>;
}

export interface TacticalFeatureCardFieldPayload {
  field_key: string;
  label: string;
  description?: string | null;
  section_id?: number | null;
  field_type: TacticalFeatureCardFieldType;
  qual_id: number;
  field_type_kls_id?: number | null;
  kls_id?: number | null;
  is_required?: boolean;
  is_readonly?: boolean;
  display_order?: number;
  is_active?: boolean;
}

export interface TacticalFeatureCardFieldUpdatePayload {
  field_key?: string;
  label?: string;
  description?: string | null;
  section_id?: number | null;
  field_type?: TacticalFeatureCardFieldType;
  qual_id?: number;
  field_type_kls_id?: number | null;
  kls_id?: number | null;
  is_required?: boolean;
  is_readonly?: boolean;
  display_order?: number;
  is_active?: boolean;
  visibility_rules?: Record<string, unknown>;
  validation_rules?: Record<string, unknown>;
}

export const tacticalFeatureCardApi = {
  listFieldWidgetTypes: () =>
    get<CardFieldWidgetTypeItem[]>("/tactical-feature-card-templates/meta/field-widget-types"),

  list: (params?: {
    status?: string;
    feature_kind?: string;
    global_kind_only?: boolean;
    is_active?: boolean;
    skip?: number;
    limit?: number;
  }) => get<TacticalFeatureCardTemplateListResponse>("/tactical-feature-card-templates", params ?? {}),

  get: (id: number) => get<TacticalFeatureCardTemplateWithDetails>(`/tactical-feature-card-templates/${id}`),

  create: (body: TacticalFeatureCardTemplateCreatePayload) =>
    post<TacticalFeatureCardTemplateWithDetails>("/tactical-feature-card-templates", body),

  update: (id: number, body: TacticalFeatureCardTemplateUpdatePayload) =>
    put<TacticalFeatureCardTemplateWithDetails>(`/tactical-feature-card-templates/${id}`, body),

  publish: (id: number, body?: CardTemplatePublishRequest) =>
    post<CardTemplatePublishResponse>(`/tactical-feature-card-templates/${id}/publish`, body ?? {}),

  archive: (id: number, body?: CardTemplatePublishRequest) =>
    post<CardTemplatePublishResponse>(`/tactical-feature-card-templates/${id}/archive`, body ?? {}),

  restore: (id: number, body?: CardTemplateRestoreRequest) =>
    post<CardTemplatePublishResponse>(`/tactical-feature-card-templates/${id}/restore`, body ?? {}),

  delete: (id: number) => del<void>(`/tactical-feature-card-templates/${id}`),

  clone: (templateId: number, newCode: string) =>
    post<TacticalFeatureCardTemplateWithDetails>(
      `/tactical-feature-card-templates/${templateId}/clone?new_code=${encodeURIComponent(newCode)}`,
      {},
    ),

  /** JSON-пакет schema geopulseml.tactical_feature_card_template_export v1 */
  exportTemplateJson: (templateId: number) =>
    get<Record<string, unknown>>(`/tactical-feature-card-templates/${templateId}/export`),

  exportTemplatesBulk: (templateIds: number[]) =>
    post<Record<string, unknown>>("/tactical-feature-card-templates/export", {
      template_ids: templateIds,
    }),

  importTemplatesJson: (
    bundle: Record<string, unknown>,
    onConflict: "error" | "skip" | "suffix" = "error",
  ) => {
    const q = new URLSearchParams({ on_conflict: onConflict });
    return post<CardTemplateImportResponse>(
      `/tactical-feature-card-templates/import?${q.toString()}`,
      bundle,
    );
  },

  addSection: (templateId: number, body: TacticalFeatureCardSectionPayload) =>
    post<TacticalFeatureCardTemplateWithDetails>(`/tactical-feature-card-templates/${templateId}/sections`, body),

  updateSection: (templateId: number, sectionId: number, body: TacticalFeatureCardSectionUpdatePayload) =>
    put<TacticalFeatureCardTemplateWithDetails>(`/tactical-feature-card-templates/${templateId}/sections/${sectionId}`, body),

  deleteSection: (templateId: number, sectionId: number) =>
    del<TacticalFeatureCardTemplateWithDetails>(`/tactical-feature-card-templates/${templateId}/sections/${sectionId}`),

  addField: (templateId: number, body: TacticalFeatureCardFieldPayload) =>
    post<TacticalFeatureCardTemplateWithDetails>(`/tactical-feature-card-templates/${templateId}/fields`, body),

  updateField: (templateId: number, fieldId: number, body: TacticalFeatureCardFieldUpdatePayload) =>
    put<TacticalFeatureCardTemplateWithDetails>(`/tactical-feature-card-templates/${templateId}/fields/${fieldId}`, body),

  deleteField: (templateId: number, fieldId: number) =>
    del<TacticalFeatureCardTemplateWithDetails>(`/tactical-feature-card-templates/${templateId}/fields/${fieldId}`),
};
